import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/site";
import { useAuth } from "@/contexts/AuthContext";
import { setToken } from "@/lib/authToken";
import Seo from "@/lib/seo/Seo";

/* Handles the post-Google-OAuth landing — exchanges session_id (URL fragment)
 * for a session_token, stores it in sessionStorage AND a cookie, then navigates to /admin.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sid = match ? decodeURIComponent(match[1]) : null;

    if (!sid) {
      navigate("/admin/login", { replace: true, state: { error: "No session ID returned from Google. Please try again." } });
      return;
    }

    (async () => {
      try {
        const r = await axios.post(
          `${API_BASE}/auth/session`,
          { session_id: sid },
          { headers: { "X-Session-ID": sid }, withCredentials: true }
        );
        // Persist token to sessionStorage + axios default header so subsequent
        // /api/auth/me and /api/admin/* calls authenticate even if the cookie is blocked.
        if (r.data?.session_token) setToken(r.data.session_token);
        setUser({ ...r.data, is_admin: true });
        window.history.replaceState(null, "", "/admin");
        navigate("/admin", { replace: true });
      } catch (err) {
        const detail = err?.response?.data?.detail;
        const msg = detail
          || (err?.response?.status === 403
                ? "This Google account is not authorized to access the Swipetronics admin dashboard."
                : "Sign-in failed. Please try again.");
        navigate("/admin/login", { replace: true, state: { error: msg } });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--swipe-navy)] text-white" data-prerender-ready="true">
      <Seo title="Signing in — Swipetronics" private />
      <div className="text-center">
        <div className="w-12 h-12 mx-auto rounded-full border-2 border-white/20 border-t-[var(--swipe-accent)] animate-spin" />
        <p className="mt-4 text-sm text-white/70">Signing you in…</p>
      </div>
    </div>
  );
}
